import { useState, useEffect, useCallback } from 'react';
import { fetchAgents, createAgent as apiCreateAgent } from './agent-api';
import { fetchProviders, fetchProviderModels } from './provider-api';
import type { Agent } from './agent-types';
import type { Provider, ProviderModel } from './provider-types';

const DEFAULT_AGENT_NAME = 'Default';

// Slug patterns for provider and model matching (case-insensitive)
const BEDROCK_SLUGS = ['bedrock', 'aws-bedrock', 'amazon-bedrock'];
const SONNET_PATTERN = /sonnet/i;
const CLAUDE_PATTERN = /claude/i;

// Preferred model slugs in priority order (highest version first, non-1M preferred)
const PREFERRED_MODEL_SLUGS = [
  'claude-4.6-sonnet-bedrock',
  'claude-4.6-sonnet-bedrock-1m',
  'claude-4.5-sonnet-bedrock',
  'claude-4.5-sonnet-bedrock-1m',
  'claude-4-sonnet-bedrock',
  'claude-4-sonnet-bedrock-1m',
  'claude-3.7-sonnet-bedrock',
  'claude-4-sonnet',
  'claude-3.7-sonnet',
];

/**
 * Search across all providers for the best provider + model combination.
 * Priority:
 *   1. Bedrock provider with a Sonnet model
 *   2. Any provider with a Sonnet model
 *   3. Platform-managed provider with its default model
 *   4. Any provider with any active model
 */
async function findBestProviderAndModel(
  wid: string,
  providers: Provider[],
): Promise<{ provider: Provider; model: ProviderModel }> {

  // Collect models per provider (use embedded if available, otherwise fetch)
  const providerModels = new Map<number, ProviderModel[]>();
  for (const p of providers) {
    let models = p.providerModels?.filter((m) => m.isActive) ?? [];
    if (models.length === 0) {
      try {
        models = (await fetchProviderModels(wid, p.id)).filter((m) => m.isActive);
      } catch {
        // Skip providers whose models can't be fetched
      }
    }
    if (models.length > 0) {
      providerModels.set(p.id, models);
    }
  }

  const isBedrock = (p: Provider) => {
    const slug = (p.providerTemplate?.slug ?? p.displayName ?? '').toLowerCase();
    return BEDROCK_SLUGS.some((b) => slug.includes(b));
  };

  // Pick the best Sonnet model by checking preferred slugs in order (4.6 → 4.5 → 4 → 3.7),
  // then falling back to any model whose slug/name contains "sonnet"
  const findSonnet = (models: ProviderModel[]): ProviderModel | undefined => {
    for (const slug of PREFERRED_MODEL_SLUGS) {
      const match = models.find((m) => m.slug === slug);
      if (match) return match;
    }
    return models.find((m) => SONNET_PATTERN.test(m.slug) || SONNET_PATTERN.test(m.name));
  };

  const findClaude = (models: ProviderModel[]) =>
    models.find((m) => CLAUDE_PATTERN.test(m.slug) || CLAUDE_PATTERN.test(m.name));

  // 1. Bedrock provider + Sonnet model
  for (const p of providers) {
    if (!isBedrock(p)) continue;
    const models = providerModels.get(p.id) ?? [];
    const sonnet = findSonnet(models);
    if (sonnet) {
      return { provider: p, model: sonnet };
    }
    // Bedrock but no sonnet — try any Claude model on Bedrock
    const claude = findClaude(models);
    if (claude) {
      return { provider: p, model: claude };
    }
  }

  // 2. Any provider with a Sonnet model
  for (const p of providers) {
    const models = providerModels.get(p.id) ?? [];
    const sonnet = findSonnet(models);
    if (sonnet) {
      return { provider: p, model: sonnet };
    }
  }

  // 3. Platform-managed provider with default or first model
  const platform = providers.find((p) => p.isPlatformManaged);
  if (platform) {
    const models = providerModels.get(platform.id) ?? [];
    const model =
      models.find((m) => m.isDefault) ??
      findClaude(models) ??
      models[0];
    if (model) {
      return { provider: platform, model };
    }
  }

  // 4. Any provider with any model
  for (const p of providers) {
    const models = providerModels.get(p.id) ?? [];
    if (models.length > 0) {
      const model = models.find((m) => m.isDefault) ?? models[0];
      return { provider: p, model };
    }
  }

  throw new Error('No providers with active models available. Please configure a provider in Settings.');
}

/**
 * Ensures a "Default" agent exists for the workspace.
 * If no agents exist, auto-creates one targeting Bedrock Sonnet.
 */
export function useDefaultAgent(workspaceId: string | null) {
  const [defaultAgent, setDefaultAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolve = useCallback(async (wid: string) => {
    setLoading(true);
    setError(null);

    try {
      // 1. Fetch existing agents
      const { agents } = await fetchAgents(wid);

      // 2. Look for agent named "Default", then isDefault flag, then first non-archived
      const existing =
        agents.find((a) => a.name === DEFAULT_AGENT_NAME && !a.archivedAt) ??
        agents.find((a) => a.isDefault && !a.archivedAt) ??
        agents.find((a) => !a.archivedAt);

      if (existing) {
        setDefaultAgent(existing);
        return;
      }

      // 3. No usable agents — create "Default" with best provider/model
      const providers = await fetchProviders(wid);
      if (providers.length === 0) {
        throw new Error('No providers available. Please configure a provider in Settings.');
      }

      const { provider, model } = await findBestProviderAndModel(wid, providers);

      const agent = await apiCreateAgent(wid, {
        name: DEFAULT_AGENT_NAME,
        description: 'Default AI assistant',
        config: {
          systemPrompt: 'You are a helpful AI assistant.',
          maxTokens: 8192,
          temperature: 0.7,
        },
        providerId: provider.id,
        providerModelId: model.id,
      });

      setDefaultAgent(agent);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set up default agent';
      setError(message);
      console.error('[useDefaultAgent]', message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (workspaceId) {
      resolve(workspaceId);
    }
  }, [workspaceId, resolve]);

  const retry = useCallback(() => {
    if (workspaceId) {
      resolve(workspaceId);
    }
  }, [workspaceId, resolve]);

  return { defaultAgent, loading, error, retry };
}
